import { useState } from "react";
import { Download, FileSpreadsheet, FileText, FileType } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { exportRows, exportSheets } from "@/lib/export";
import { exportPdf } from "@/lib/pdf";

/**
 * Compact export menu (Excel + CSV + optional PDF) used in report toolbars.
 *
 * Either pass `rows` + `columns` (single dataset) OR `sheets` (multi-sheet).
 *
 * Optional `pdf` prop = { title, subtitle, columns, totals, filename, orientation }
 * — when provided, an "Esporta PDF" entry is added to the menu using the
 * professional PDF report layout. PDF is single-section only.
 */
export function ExportButtons({
  rows,
  columns,
  sheets,
  filenameBase,
  sheetName = "Dati",
  disabled,
  variant = "outline",
  size = "sm",
  testId = "export",
  pdf,
}) {
  const [busy, setBusy] = useState(false);

  const isEmpty = sheets
    ? sheets.every((s) => !s.rows || s.rows.length === 0)
    : !rows || rows.length === 0;

  const handle = async (format) => {
    try {
      setBusy(true);
      if (format === "pdf") {
        if (!pdf) throw new Error("PDF config missing");
        exportPdf({
          title: pdf.title,
          subtitle: pdf.subtitle,
          columns: pdf.columns || columns,
          rows: pdf.rows || rows,
          totals: pdf.totals,
          filename: pdf.filename || filenameBase,
          orientation: pdf.orientation || "landscape",
        });
        toast.success("PDF esportato");
      } else if (sheets && sheets.length) {
        exportSheets({ sheets, filenameBase, format });
        toast.success(format === "xlsx" ? "Excel esportato" : "CSV esportato");
      } else {
        exportRows({ rows, columns, sheetName, filenameBase, format });
        toast.success(format === "xlsx" ? "Excel esportato" : "CSV esportato");
      }
    } catch (e) {
      console.error(e);
      toast.error("Errore durante l'esportazione");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          disabled={disabled || isEmpty || busy}
          data-testid={`${testId}-trigger`}
        >
          <Download size={14} strokeWidth={1.8} className="mr-1.5" />
          Esporta
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem
          data-testid={`${testId}-xlsx`}
          onClick={() => handle("xlsx")}
        >
          <FileSpreadsheet size={14} strokeWidth={1.8} className="mr-2" />
          Esporta Excel (.xlsx)
        </DropdownMenuItem>
        {pdf && (
          <DropdownMenuItem
            data-testid={`${testId}-pdf`}
            onClick={() => handle("pdf")}
          >
            <FileType size={14} strokeWidth={1.8} className="mr-2" />
            Esporta PDF
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          data-testid={`${testId}-csv`}
          onClick={() => handle("csv")}
        >
          <FileText size={14} strokeWidth={1.8} className="mr-2" />
          Esporta CSV
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
